// ==============================|| DARK MODE - INLINE COLOR REMAP ||============================== //

// Most screens (and kahuna-base-react-components: KSpan, KButton, KInput, ...) colour elements
// with inline styles, which the MUI palette never reaches. In dark mode the light-mode colours
// used across the app are remapped here instead of touching every component.
//
// It works on the style attribute as the browser serialises it: React writes `color: "#111"`
// and the attribute reads `color: rgb(17, 17, 17);`, so every colour below is matched in that
// form (named colours like "black" stay as written). Only !important beats an inline style.
//
// Text colours go through the --k-dm-text-* variables, which every inline background resets for
// its content: backgrounds remapped to dark surfaces get light text, every other background
// (brand / status colours, often computed with alpha, and black buttons that turn light) keeps
// the dark text it has in light mode.

// palette: --k-dm-* variables in styles/dark-mode.css
const SURFACE = "var(--k-dm-surface)"
const SURFACE_SUBTLE = "var(--k-dm-surface-subtle)"
const SURFACE_MUTED = "var(--k-dm-surface-muted)"
const SURFACE_STRONG = "var(--k-dm-surface-strong)"
const INVERTED = "var(--k-dm-inverted)"
const INVERTED_HOVER = "var(--k-dm-inverted-hover)"
const BORDER = "var(--k-dm-border)"
const BORDER_STRONG = "var(--k-dm-border-strong)"

// same values as html.dark in styles/dark-mode.css
const TEXT_VARS_DARK = {
  "--k-dm-text-strong": "#EDEDED",
  "--k-dm-text-medium": "#D4D4D4",
  "--k-dm-text-muted": "#A3A3A3",
  "--k-dm-text-faint": "#7A7A7A",
  "--k-dm-text-on-dark": "#FFFFFF",
  "--k-dm-icon-dark-filter": "invert(0.93)",
  color: "#EDEDED"
}

// light-mode text colours, for content of backgrounds that are not remapped to a dark surface;
// dark <img> icons stay dark there too (styles/dark-mode.css)
const TEXT_VARS_LIGHT = {
  "--k-dm-text-strong": "#111111",
  "--k-dm-text-medium": "#374151",
  "--k-dm-text-muted": "#666666",
  "--k-dm-text-faint": "#9CA3AF",
  "--k-dm-text-on-dark": "#FFFFFF",
  "--k-dm-icon-dark-filter": "none",
  color: "#111111"
}

// prettier-ignore
const BACKGROUND_MAP: Record<string, string[]> = {
  [SURFACE]: ["#FFFFFF", "white", "#FCFCFD"],
  [SURFACE_SUBTLE]: [
    "#FAFAFA", "#F9FAFB", "#F9F9F9", "#F8F9FA", "#F8F8F8", "#F7F7F7", "#F6F8FA", "#F5F5F7",
    "#F5F5F5", "#F4F4F4", "#F3F6F9", "#F3F4F6", "#F3F3F3", "#F2F2F2", "whitesmoke"
  ],
  [SURFACE_MUTED]: [
    "#F0F0F0", "#EEF0F3", "#EEEEEE", "#E8E8E8", "#E7E7E7", "#E5E7EB", "#E5E5E5", "#E5EAF2",
    "#E2E2E2", "#E0E0E0"
  ],
  [SURFACE_STRONG]: [
    "#DDDDDD", "#DAE2ED", "#D6D6D6", "#D4D4D4", "#D4D4D8", "#D1D5DB", "#D0D0D0", "#CCCCCC",
    "#C7D0DD", "#C5C5C5", "#C0C0C0", "#BDBDBD", "lightgray", "lightgrey", "gainsboro"
  ],
  // black buttons / selected states turn light so they keep standing out
  [INVERTED]: [
    "#000000", "black", "#111111", "#0F172A", "#111827", "#131920", "#1A1A1A", "#1C2025",
    "#1D2630", "#212121", "#222222"
  ],
  [INVERTED_HOVER]: ["#303740", "#333333", "#374151", "#3E4853", "#434D5B", "#444444", "#4B5563", "#555555"],
  // pale status tints (success / error / warning / info chips and alerts) become dark tints
  "var(--k-dm-tint-green)": ["#D4EDDA", "#C3E6CB", "#E8F5E9"],
  "var(--k-dm-tint-green-strong)": ["#D4EBD7"],
  "var(--k-dm-tint-emerald)": ["#D1FAE5", "#ECFDF5", "#DCFCE7"],
  "var(--k-dm-tint-red)": ["#F8D7DA", "#F1C2C7", "#F5C6CB"],
  "var(--k-dm-tint-red-strong)": ["#F5DFE1"],
  "var(--k-dm-tint-rose)": ["#FFEBEE", "#FEE2E2", "#FDECEC", "#FCA5A5"],
  "var(--k-dm-tint-yellow)": ["#FFF3CD", "#FFF9E6", "#FFEAA7", "#FFF8E1", "#FEF3C7", "#FFECB5"],
  "var(--k-dm-tint-yellow-strong)": ["#F5F0D9"],
  "var(--k-dm-tint-sky)": ["#E3F2FD", "#E0F2FE", "#D1ECF1", "#ECFEFF"],
  "var(--k-dm-tint-sky-strong)": ["#C8E1F5"],
  "var(--k-dm-tint-blue)": ["#CFE2FF", "#DBEAFE", "#D1E7FD", "#DAECFF", "#B6DAFF"],
  "var(--k-dm-tint-indigo)": ["#E2E3FF", "#E0E7FF", "#EDE9FE", "#DDD6FE", "#C4B5FD", "#A5B4FC"],
  "var(--k-dm-tint-purple)": ["#F3E5F5", "#F3E8FF", "#D8B4FE"],
  "var(--k-dm-tint-purple-strong)": ["#E4D4EB"],
  "var(--k-dm-tint-pink)": ["#FCE7F3", "#FDF2F8"],
  "var(--k-dm-tint-orange)": ["#FFF7ED", "#FED7AA"],
  "var(--k-dm-tint-lime)": ["#EDF9D7", "#D9F4A9"]
}

// prettier-ignore
const TEXT_MAP: Record<string, string[]> = {
  "var(--k-dm-text-strong)": [
    "#000000", "black", "#111111", "#0F172A", "#111827", "#131920", "#1A1A1A", "#1C2025",
    "#1D2630", "#1F2937", "#212121", "#222222"
  ],
  "var(--k-dm-text-medium)": [
    "#303740", "#333333", "#373D3F", "#374151", "#3E4853",
    "#434D5B", "#444444", "#4B5563", "#555555"
  ],
  "var(--k-dm-text-muted)": [
    "#5B6B79", "#666666", "#6B7280", "#737373", "gray", "#808080", "#848484", "#888888",
    "#8996A4", "#8C8C8C", "#999999", "#9CA3AF"
  ],
  "var(--k-dm-text-faint)": [
    "#B2B2B2", "#B3B3B3", "#B5B5B5", "#BDBDBD", "#BEBEBE", "#C0C0C0", "#C5C5C5", "#CCCCCC",
    "#D0D0D0", "#D1D5DB", "#D4D4D4", "#D6D6D6", "lightgray", "lightgrey", "silver"
  ],
  "var(--k-dm-text-on-dark)": ["#FFFFFF", "white"],
  // dark status text that sat on the pale tints above
  "var(--k-dm-status-green)": ["#155724", "#166534", "#15803D"],
  "var(--k-dm-status-emerald)": ["#105A37", "#065F46"],
  "var(--k-dm-status-red)": ["#721C24", "#8B0000", "#991B1B"],
  "var(--k-dm-status-yellow)": ["#856404", "#7A5B00"],
  "var(--k-dm-status-orange)": ["#92400E", "#9A3412"],
  "var(--k-dm-status-cyan)": ["#0C5460", "#0E7490"],
  "var(--k-dm-status-blue)": ["#084298", "#003A75", "#0369A1", "#1E40AF"],
  // other dark accent text (links, flags, badges) lifted to a lighter tone of the same hue
  "#E5938A": ["#C0392B"],
  "#EA858B": ["#C8232C", "#B91C1C"],
  "#82A4ED": ["#124BB3", "#1D4ED8", "#2563EB"],
  "#94B8DB": ["#336699"],
  "#9C9CD4": ["#3D3D8C", "#3730A3", "#4A3AA7"],
  "#AA82ED": ["#7C3AED", "#6D28D9", "#7E22CE"],
  "#ED82AF": ["#BE185D", "#9D174D"]
}

// prettier-ignore
const BORDER_MAP: Record<string, string[]> = {
  [BORDER]: [
    "#F0F0F0", "#F2F2F2", "#F3F3F3", "#F3F4F6", "#F5F5F5", "#EEEEEE", "#E8E8E8", "#E7E7E7",
    "#E5E7EB", "#E5E5E5", "#E2E2E2", "#E0E0E0", "#DDDDDD", "#DBE0E5", "#D4D4D4", "#D1D5DB",
    "#D0D0D0", "#CCCCCC", "lightgray", "lightgrey"
  ],
  [BORDER_STRONG]: ["#000000", "black", "#111111", "#222222", "#333333"]
}

const BORDER_STYLES = ["solid", "dashed", "dotted"]

// "#111" -> "rgb(17, 17, 17)", the form the style attribute holds; names stay as they are
const toAttributeColor = (color: string) => {
  if (!color.startsWith("#")) return color
  const hex = color.slice(1)
  const [r, g, b] = [0, 2, 4].map((index) => parseInt(hex.slice(index, index + 2), 16))
  return `rgb(${r}, ${g}, ${b})`
}

const styleAttr = (declaration: string) => `html.dark [style*="${declaration}"]`

const textSelectors = (color: string) => {
  const value = toAttributeColor(color)
  const alpha = withAlpha(value)
  // the leading space keeps background-color / border-color from matching
  return [
    `html.dark [style^="color: ${value};"]`,
    styleAttr(` color: ${value};`),
    ...(alpha ? [`html.dark [style^="color: ${alpha}"]`, styleAttr(` color: ${alpha}`)] : [])
  ]
}

// colours with an alpha channel ("#F2FE6799") are serialised as rgba(r, g, b, a)
const withAlpha = (value: string) => (value.startsWith("rgb(") ? `rgba(${value.slice(4, -1)}, ` : null)

// black only turns light when opaque; translucent black is a hover / selection tint
const isInverted = (target: string) => target === INVERTED || target === INVERTED_HOVER

const opaqueBackgroundSelectors = (color: string) => {
  const value = toAttributeColor(color)
  return [styleAttr(`background: ${value};`), styleAttr(`background-color: ${value};`)]
}

const backgroundSelectors = (color: string) => {
  const alpha = withAlpha(toAttributeColor(color))
  return [
    ...opaqueBackgroundSelectors(color),
    ...(alpha ? [styleAttr(`background: ${alpha}`), styleAttr(`background-color: ${alpha}`)] : [])
  ]
}

const borderSelectors = (color: string) => {
  const value = toAttributeColor(color)
  return [...BORDER_STYLES.map((style) => styleAttr(`${style} ${value};`)), styleAttr(`border-color: ${value};`)]
}

const svgSelectors = (color: string, attribute: "fill" | "stroke") => [
  `html.dark svg[${attribute}="${color}" i]`,
  `html.dark svg [${attribute}="${color}" i]`
]

// alpha values below 0.5 as the browser writes them: two decimals, or three when a hex alpha
// byte ("#11111116") does not round-trip through two (0.086)
const LOW_ALPHAS = (() => {
  const alphas = new Set<string>()
  for (let hundredths = 1; hundredths < 50; hundredths++) alphas.add(String(hundredths / 100))
  for (let byte = 1; byte < 128; byte++) {
    const twoDecimals = Math.round((byte / 255) * 100) / 100
    const value = Math.round(twoDecimals * 255) === byte ? twoDecimals : Math.round((byte / 255) * 1000) / 1000
    alphas.add(String(value))
  }
  return [...alphas]
})()

const rule = (selectors: string[], declarations: Record<string, string>) => {
  const body = Object.entries(declarations)
    .map(([property, value]) => `${property}: ${value} !important;`)
    .join(" ")
  return `${selectors.join(",\n")} { ${body} }`
}

const varsRule = (selectors: string[], vars: Record<string, string>) => {
  const body = Object.entries(vars)
    .map(([name, value]) => `${name}: ${value};`)
    .join(" ")
  return `${selectors.join(",\n")} { ${body} }`
}

const buildDarkModeInlineStyles = () => {
  const rules: string[] = []

  Object.entries(BACKGROUND_MAP).forEach(([target, colors]) => {
    rules.push(
      rule(colors.flatMap(isInverted(target) ? opaqueBackgroundSelectors : backgroundSelectors), { background: target })
    )
  })

  Object.entries(TEXT_MAP).forEach(([target, colors]) => {
    rules.push(rule(colors.flatMap(textSelectors), { color: target }))
    // icons (iconsax etc.) take the same colours as fill / stroke attributes
    const hexOrNamed = colors.flatMap((color) =>
      color.startsWith("#") && color.slice(1, 4).repeat(2) === color.slice(1) ? [color, color.slice(0, 4)] : [color]
    )
    rules.push(
      rule(
        hexOrNamed.flatMap((color) => svgSelectors(color, "fill")),
        { fill: target }
      )
    )
    rules.push(
      rule(
        hexOrNamed.flatMap((color) => svgSelectors(color, "stroke")),
        { stroke: target }
      )
    )
  })

  // translucent black text (secondary or disabled copy, e.g. "rgba(1, 1, 1, 0.4)") reads as muted text
  rules.push(
    rule(
      ["rgba(0, 0, 0, ", "rgba(1, 1, 1, ", "rgba(17, 17, 17, "].flatMap((value) => [
        `html.dark [style^="color: ${value}"]`,
        styleAttr(` color: ${value}`)
      ]),
      { color: "var(--k-dm-text-muted)" }
    )
  )

  Object.entries(BORDER_MAP).forEach(([target, colors]) => {
    rules.push(rule(colors.flatMap(borderSelectors), { "border-color": target }))
  })
  rules.push(
    rule(
      BORDER_STYLES.flatMap((style) => [styleAttr(`${style} rgba(0, 0, 0, `), styleAttr(`${style} rgba(17, 17, 17, `)]),
      { "border-color": "rgba(255, 255, 255, 0.12)" }
    )
  )

  // text colour of each background's content, in increasing priority (later rules win):
  // any inline background keeps light-mode text, remapped dark surfaces switch to light text,
  // inverted (formerly black) backgrounds are light again and also turn white text dark
  rules.push(
    varsRule(
      [
        styleAttr("background: rgb"),
        styleAttr("background-color: rgb"),
        // gradients (pastel "Create Task" buttons) are not remapped either
        styleAttr("background: linear-gradient("),
        styleAttr("background-image: linear-gradient(")
      ],
      TEXT_VARS_LIGHT
    )
  )
  const darkSurfaces = Object.entries(BACKGROUND_MAP)
    .filter(([target]) => target !== INVERTED && target !== INVERTED_HOVER)
    .flatMap(([, colors]) => colors)
  rules.push(
    varsRule(
      [
        ...darkSurfaces.flatMap(backgroundSelectors),
        // transparent and faint black / white overlays show the dark surface behind them
        ...["rgba(0, 0, 0, ", "rgba(17, 17, 17, ", "rgba(255, 255, 255, "].flatMap((value) => [
          styleAttr(`background: ${value}`),
          styleAttr(`background-color: ${value}`)
        ])
      ],
      TEXT_VARS_DARK
    )
  )
  // faint colour tints ("#c0392b1A", rgba(255, 0, 0, 0.2)) sit on the dark surface and read dark too
  rules.push(
    varsRule(
      LOW_ALPHAS.flatMap((alpha) => [
        `html.dark [style*="background: rgba("][style*=", ${alpha});"]`,
        `html.dark [style*="background-color: rgba("][style*=", ${alpha});"]`
      ]),
      TEXT_VARS_DARK
    )
  )
  rules.push(
    varsRule([...BACKGROUND_MAP[INVERTED], ...BACKGROUND_MAP[INVERTED_HOVER]].flatMap(opaqueBackgroundSelectors), {
      ...TEXT_VARS_LIGHT,
      "--k-dm-text-on-dark": "#111111",
      // white <img> icons turn dark as well (styles/dark-mode.css)
      "--k-dm-icon-white-filter": "invert(0.93)"
    })
  )

  // translucent white panels would otherwise read as grey film over the dark page
  rules.push(
    rule([styleAttr("background: rgba(255, 255, 255, "), styleAttr("background-color: rgba(255, 255, 255, ")], {
      background: SURFACE_SUBTLE
    })
  )

  return rules.join("\n")
}

const darkModeInlineStyles = buildDarkModeInlineStyles()

export default darkModeInlineStyles
